import React, { useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../../assets/image/logo1.png';

const PaymentReceipt = ({ formData, paymentDetails, onDownloadComplete }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [fullPaymentDetails, setFullPaymentDetails] = useState({});

  // Fetch complete payment details from database
  useEffect(() => {
    const fetchPaymentDetailsFromDB = async () => {
      try {
        const transactionId = paymentDetails.transactionId || formData.request_unique_id;
        if (!transactionId) {
          console.error('No transaction ID available');
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/get_payment_details.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ transactionid: transactionId }),
        });

        const result = await response.json();
        
        if (result.status === 'success') {
          // Merge with existing paymentDetails
          setFullPaymentDetails({
            ...paymentDetails,
            ...result.data,
            transactionId: transactionId,
          });
        } else {
          // Use the paymentDetails from props if DB fetch fails
          setFullPaymentDetails(paymentDetails);
        }
      } catch (error) {
        console.error('Error fetching payment details:', error);
        setFullPaymentDetails(paymentDetails);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentDetailsFromDB();
  }, [formData.request_unique_id, paymentDetails]);

  const generatePDF = () => {
    if (isLoading) {
      console.log('Still loading payment details...');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    
    // ============ PAGE 1: HEADER AND APPLICANT DETAILS ============
    
    // Add logo
    if (logo) {
      doc.addImage(logo, 'PNG', margin, 10, 40, 20);
    }

    // Title
    doc.setFontSize(20);
    doc.text('PAYMENT RECEIPT', pageWidth / 2, 25, { align: 'center' });

    // Receipt Details
    doc.setFontSize(10);
    doc.text(`Receipt No: ${fullPaymentDetails.receiptNumber || 'PP' + Date.now()}`, margin, 45);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, 52);
    doc.text(`Time: ${new Date().toLocaleTimeString()}`, margin, 59);

    // Horizontal line
    doc.setLineWidth(0.5);
    doc.line(margin, 65, pageWidth - margin, 65);

    // Application Details Header
    doc.setFontSize(16);
    doc.text('APPLICATION DETAILS', pageWidth / 2, 75, { align: 'center' });

    // Applicant Information Table
    const applicantData = [
      ['Applicant Name:', formData.applicantName || 'N/A'],
      ['Mobile No:', formData.applicantMobile || 'N/A'],
      ['Email:', formData.applicantEmail || 'N/A'],
      ['Address:', formData.applicantAddress || 'N/A'],
      ['Property Number Type:', formData.PropertyNumberType || 'N/A'],
      ['Village:', formData.village || 'N/A']
    ];

    autoTable(doc, {
      startY: 80,
      head: [['Field', 'Details']],
      body: applicantData,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold' },
        1: { cellWidth: 110 }
      },
      margin: { left: margin, right: margin }
    });

    // Property Details Table
    const propertyData = [
      ['Division:', formData.division || 'N/A'],
      ['District:', formData.district || 'N/A'],
      ['Taluka:', formData.taluka || 'N/A'],
      ['Survey No:', formData.surveyNumber || 'N/A'],
      ['Area:', formData.area || 'N/A'],
      ['Application Type:', formData.certification_name || formData.formType || 'N/A']
    ];

    const firstTableEndY = doc.lastAutoTable.finalY + 10;
    
    autoTable(doc, {
      startY: firstTableEndY,
      head: [['Property Details', '']],
      body: propertyData,
      theme: 'grid',
      headStyles: { fillColor: [39, 174, 96], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold' },
        1: { cellWidth: 110 }
      },
      margin: { left: margin, right: margin }
    });

    // Add page footer for page 1
    doc.setFontSize(8);
    doc.text('Page 1 of 2', pageWidth / 2, 280, { align: 'center' });
    
    // ============ PAGE 2: PAYMENT DETAILS ============
    doc.addPage();
    
    // Payment Details Header
    doc.setFontSize(16);
    doc.text('PAYMENT DETAILS', pageWidth / 2, 20, { align: 'center' });
    
    // Payment Details Table - Updated with database values
    const paymentData = [
      ['Payment Method:', 'Online'],
      ['Payment Mode:', fullPaymentDetails.payment_mode || fullPaymentDetails.paymentMethod || 'Credit Card/Debit Card/Net Banking'],
      ['Amount:', `₹${fullPaymentDetails.Amount || fullPaymentDetails.amount || '700.00'}`],
      ['Transaction Fee:', `₹${fullPaymentDetails.trans_fee || '0.00'}`],
      ['Service Tax:', `₹${fullPaymentDetails.service_tax || '0.00'}`],
      ['Total Amount:', `₹${(
        parseFloat(fullPaymentDetails.Amount || fullPaymentDetails.amount || 700) + 
        parseFloat(fullPaymentDetails.trans_fee || 0) + 
        parseFloat(fullPaymentDetails.service_tax || 0)
      ).toFixed(2)}`],
      ['Transaction ID:', fullPaymentDetails.transactionId || 'N/A'],
      ['Tracking ID:', fullPaymentDetails.tracking_id || 'N/A'],
      ['Bank Reference No:', fullPaymentDetails.bank_ref_no || 'N/A'],
      ['UTR/Cheque No:', fullPaymentDetails.utrChequeNo || fullPaymentDetails.bank_ref_no || 'N/A'],
      ['Order Status:', fullPaymentDetails.order_status || 'Success'],
      ['Payment Date:', new Date(fullPaymentDetails.updated_at || Date.now()).toLocaleDateString()],
      ['Payment Time:', new Date(fullPaymentDetails.updated_at || Date.now()).toLocaleTimeString()]
    ];

    autoTable(doc, {
      startY: 30,
      head: [['Payment Information', '']],
      body: paymentData,
      theme: 'grid',
      headStyles: { fillColor: [142, 68, 173], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 70, fontStyle: 'bold' },
        1: { cellWidth: 100 }
      },
      margin: { left: margin, right: margin }
    });

    // Terms and Conditions Section
    const paymentTableEndY = doc.lastAutoTable.finalY + 15;
    
    doc.setFontSize(12);
    doc.text('Terms and Conditions:', margin, paymentTableEndY);
    
    doc.setFontSize(10);
    const terms = [
      '1. This receipt is test version computer generated and does not require signature.',
      '2. Please keep this receipt for future reference and correspondence.',
      '3. Transaction ID should be quoted in all future communications.',
      '4. For any queries regarding this payment, contact the support team.',
      '5. Payment once made cannot be refunded unless under exceptional circumstances.',
      '6. This receipt is valid only for the transaction mentioned above.',
      '7. Regional Plan Department is not responsible for any discrepancies in the property details provided by the applicant.'
    ];
    
    let yPos = paymentTableEndY + 10;
    terms.forEach((term, index) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(term, margin + 5, yPos);
      yPos += 7;
    });

    // Contact Information
    yPos += 10;
    doc.setFontSize(10);
    doc.text('For any queries, please contact:', margin, yPos);
    doc.text('Email: support@regionalplan.gov.in', margin, yPos + 7);
    doc.text('Phone: 1800-XXX-XXXX (Toll Free)', margin, yPos + 14);
    doc.text('Office Hours: 9:00 AM - 6:00 PM (Mon-Sat)', margin, yPos + 21);

    // Footer for page 2
    doc.setFontSize(8);
    doc.text('© 2025 Regional Plan Department. All rights reserved.', pageWidth / 2, 280, { align: 'center' });
    doc.text('Page 2 of 2', pageWidth / 2, 285, { align: 'center' });

    // Generate filename
    const fileName = `Receipt_${formData.applicantName?.replace(/\s+/g, '_') || 'User'}_${Date.now()}.pdf`;

    // Save PDF
    doc.save(fileName);

    // Call callback if provided
    if (onDownloadComplete) {
      onDownloadComplete();
    }
  };

  // Generate PDF when details are loaded
  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        generatePDF();
      }, 100);
    }
  }, [isLoading]);

  return (
    <div style={{ display: 'none' }}>
      {/* Optional: Show loading indicator */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <div className="spinner" style={{
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 10px'
            }}></div>
            <p>Generating receipt with payment details...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReceipt;